import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { marketingService } from '../api/services/marketingService';
import {
  detectPlatform,
  getSessionId,
  isTrackingAllowed,
  resolveCampaign,
} from '../utils/marketing';
import { LANGUAGE_KEY } from '../i18n';
import { LANGUAGES, SupportedLanguage } from '../i18n/useLanguage';
import { IOS_STORE_URL, androidStoreUrl } from './storeLinks';
import './DownloadPage.css';

const SUPPORTED_LANGS: SupportedLanguage[] = ['sq', 'en'];

const DownloadPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const trackedRef = useRef(false);
  const redirectTimer = useRef<number | null>(null);

  // Localization: a ?lang= param wins, then a previously saved choice, then the
  // visitor's browser language (influencer links are shared with people who may
  // never have opened the site, so the app default alone isn't enough).
  useEffect(() => {
    const param = searchParams.get('lang')?.toLowerCase();
    if (param && SUPPORTED_LANGS.includes(param as SupportedLanguage)) {
      if (i18n.language !== param) i18n.changeLanguage(param);
      return;
    }
    if (localStorage.getItem(LANGUAGE_KEY)) return; // respect explicit choice
    const nav = (navigator.language || '').toLowerCase();
    const detected: SupportedLanguage = nav.startsWith('en') ? 'en' : 'sq';
    if (i18n.language !== detected) i18n.changeLanguage(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLang = (code: SupportedLanguage) => {
    if (i18n.language !== code) i18n.changeLanguage(code);
  };

  const campaign = useMemo(
    () => resolveCampaign(searchParams.get('campaign')),
    [searchParams],
  );
  const platform = useMemo(() => detectPlatform(), []);
  const [redirecting, setRedirecting] = useState(
    platform === 'IOS' || platform === 'ANDROID',
  );

  const storeUrlFor = (target: 'IOS' | 'ANDROID') =>
    target === 'IOS' ? IOS_STORE_URL : androidStoreUrl(campaign);

  const goToStore = (target: 'IOS' | 'ANDROID') => {
    if (isTrackingAllowed()) {
      marketingService.trackRedirect({
        sessionId: getSessionId(),
        platform: target,
        slug: campaign ?? undefined,
      });
    }
    window.location.href = storeUrlFor(target);
  };

  // Log the visit once, then auto-redirect mobile visitors to their store.
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    if (isTrackingAllowed()) {
      marketingService.trackVisit({
        sessionId: getSessionId(),
        slug: campaign ?? undefined,
        referrer: document.referrer || undefined,
      });
    }

    if (platform === 'IOS' || platform === 'ANDROID') {
      // Small delay so the visit + redirect beacons flush before navigation.
      redirectTimer.current = window.setTimeout(() => goToStore(platform), 600);
      return () => {
        if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelRedirect = () => {
    if (redirectTimer.current) {
      window.clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
    setRedirecting(false);
  };

  const qrValue = useMemo(() => {
    const url = new URL('/download', window.location.origin);
    if (campaign) url.searchParams.set('campaign', campaign);
    return url.toString();
  }, [campaign]);

  const langToggle = (
    <div className="download-lang" role="group" aria-label="Language">
      {LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          type="button"
          className={i18n.language?.startsWith(lang.code) ? 'active' : ''}
          onClick={() => changeLang(lang.code)}
          aria-pressed={i18n.language?.startsWith(lang.code)}
        >
          {lang.code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  if (redirecting) {
    return (
      <div className="download-page">
        <Helmet>
          <title>{t('download.meta_title')}</title>
        </Helmet>
        {langToggle}
        <div className="download-redirecting">
          <div className="ap-spinner" aria-hidden />
          <p>{t('download.redirecting')}</p>
          <p>
            <a
              href={storeUrlFor(platform === 'ANDROID' ? 'ANDROID' : 'IOS')}
              onClick={(e) => {
                e.preventDefault();
                goToStore(platform === 'ANDROID' ? 'ANDROID' : 'IOS');
              }}
            >
              {t('download.tap_if_not_redirected')}
            </a>
          </p>
          <button
            type="button"
            className="download-privacy"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={cancelRedirect}
          >
            {t('download.show_options')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="download-page">
      <Helmet>
        <title>{t('download.meta_title')}</title>
        <meta name="description" content={t('download.meta_description')} />
        <html lang={i18n.language?.startsWith('en') ? 'en' : 'sq'} />
      </Helmet>
      {langToggle}

      <div className="download-card">
        <div>
          <img
            className="download-logo"
            src="/images/logos/AutoPlaceWhite.png"
            alt="AutoPlace"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src =
                '/images/logos/AutoPlace.png';
            }}
          />
          <h1
            className="download-title"
            dangerouslySetInnerHTML={{ __html: t('download.title') }}
          />
          <p className="download-subtitle">{t('download.subtitle')}</p>

          <div className="download-buttons">
            <button
              type="button"
              className="store-button"
              onClick={() => goToStore('IOS')}
            >
              <FaApple className="store-icon" />
              <span className="store-text">
                <small>{t('download.download_on')}</small>
                <strong>App Store</strong>
              </span>
            </button>

            <button
              type="button"
              className="store-button"
              onClick={() => goToStore('ANDROID')}
            >
              <FaGooglePlay className="store-icon" />
              <span className="store-text">
                <small>{t('download.get_it_on')}</small>
                <strong>Google Play</strong>
              </span>
            </button>
          </div>

          <p className="download-privacy">
            {t('download.privacy_note')}{' '}
            <a href="/privacy-policy">{t('download.privacy_link')}</a>
          </p>
        </div>

        <div className="download-qr">
          <QRCodeSVG value={qrValue} size={168} fgColor="#0f0f11" level="M" />
          <span>{t('download.scan_qr')}</span>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
